import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  BarChart3, Library, Loader2, Settings2, AlertTriangle, ShieldAlert,
  ClipboardCheck, TrendingUp, FileText, Activity,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PRIORITY_COLORS = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
};

function Stat({ label, value, tone = 'slate', hint, testid }) {
  const t = { slate: 'text-slate-900', emerald: 'text-emerald-600', red: 'text-red-600', amber: 'text-amber-600', blue: 'text-blue-600' };
  return (
    <Card className="border-slate-200"><CardContent className="py-4 px-4">
      <div className={`text-3xl font-bold ${t[tone]}`} data-testid={testid}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </CardContent></Card>
  );
}

function Section({ title, icon: Icon, right, children, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-500" /> {title}
          </h3>
          {right}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ManagementReviewPage() {
  const { user } = useContext(AuthContext);
  const canSettings = user?.role === 'admin' || user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports/management-review`);
      setData(r.data);
      setEnabled(r.data.feature_enabled !== false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (v) => {
    try {
      await axios.patch(`${API}/reports/settings`, { use_new_management_review: v });
      setEnabled(v);
      toast.success(`Management Review ${v ? 'enabled' : 'disabled'}`);
      setSettingsOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }
  if (data && data.feature_enabled === false) {
    return (
      <div className="space-y-4" data-testid="management-review-page">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-slate-700" /> Management Review
        </h1>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900 flex items-center justify-between">
            <span><strong>New Management Review is OFF.</strong> Enable it in Settings.</span>
            {canSettings && <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="mr-settings-btn"><Settings2 className="w-4 h-4 mr-1" /> Settings</Button>}
          </CardContent>
        </Card>
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} enabled={enabled} onToggle={toggle} />
      </div>
    );
  }

  const ap = data.assessment_progress;
  const g = data.gaps;
  const r = data.risks;
  const t = data.treatments;
  const ev = data.evidence;

  return (
    <div className="space-y-6" data-testid="management-review-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" /><span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span><span>Management Review</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-slate-700" /> Management Review
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Generated live from your current assessments, gaps, risks, treatments, and evidence. No duplicate data.
          </p>
          {data.generated_at && (
            <div className="text-xs text-slate-500 mt-1">Generated: {new Date(data.generated_at).toLocaleString()}</div>
          )}
        </div>
        {canSettings && <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="mr-settings-btn"><Settings2 className="w-4 h-4 mr-1" /> Settings</Button>}
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Assessment progress" value={`${ap.percentage_assessed}%`} tone="blue" hint={`${ap.assessed}/${ap.total_controls} controls`} testid="mr-assessed" />
        <Stat label="Open gaps" value={g.total} tone="amber" hint={`${g.by_priority.Critical} Critical`} testid="mr-gaps" />
        <Stat label="Open risks" value={r.total} tone="red" hint={`${r.overdue_total} overdue`} testid="mr-risks" />
        <Stat label="Evidence with files" value={ev.with_files} tone="emerald" hint={`${ev.total_requests} requests`} testid="mr-evidence" />
      </div>

      {/* Framework compliance */}
      <Section title="Framework Compliance" icon={TrendingUp} testid="mr-framework-compliance"
        right={<Link to="/master-control-library" className="text-xs text-blue-700 hover:underline">Library →</Link>}>
        <div className="grid md:grid-cols-3 gap-3">
          {data.framework_compliance.map((f) => (
            <div key={f.framework.id} className="rounded-lg border border-slate-200 p-3" data-testid={`mr-fw-${f.framework.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: f.framework.color }}>{f.framework.short_name}</span>
                <span className="text-xl font-bold text-slate-900">{f.compliance_percentage == null ? 'N/A' : `${f.compliance_percentage}%`}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{f.total_controls} controls · {f.derived_status}</div>
              <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${f.compliance_percentage || 0}%`, backgroundColor: f.framework.color }} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Gaps */}
      <Section title={`Significant Gaps (${g.significant.length})`} icon={AlertTriangle} testid="mr-significant-gaps"
        right={<Link to="/gap-engine" className="text-xs text-blue-700 hover:underline">Gap Engine →</Link>}>
        {g.significant.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No Critical or High priority gaps open.</p>
        ) : (
          <ul className="space-y-1">
            {g.significant.slice(0, 10).map((it) => (
              <li key={it.control_id} className="flex items-center gap-2 text-sm" data-testid={`mr-gap-${it.control_id}`}>
                <span className="font-mono text-xs text-slate-600 w-16">{it.control_id}</span>
                <Badge className={`${PRIORITY_COLORS[it.priority]} border text-[10px]`}>{it.priority}</Badge>
                <span className="text-slate-800 flex-1 truncate">{it.name}</span>
                <span className="text-xs text-slate-500">{it.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Risks */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Risk Status" icon={ShieldAlert} testid="mr-risk-status"
          right={<Link to="/risk-engine" className="text-xs text-blue-700 hover:underline">Risk Register →</Link>}>
          <div className="space-y-2 text-sm">
            {Object.entries(r.by_rating).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <Badge className={`${PRIORITY_COLORS[k]} border text-xs`}>{k}</Badge>
                <span className="font-mono text-slate-700">{v}</span>
              </div>
            ))}
            {Object.entries(r.by_status).length > 0 && (
              <div className="pt-2 border-t border-slate-100 text-xs text-slate-600">
                <div className="font-semibold uppercase mb-1">By status</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(r.by_status).map(([k, v]) => (
                    <span key={k}><strong>{k}:</strong> {v}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Section>

        <Section title="Overdue Risk Treatments" icon={AlertTriangle} testid="mr-overdue-risks">
          {r.overdue.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No overdue risk treatments. Great!</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {r.overdue.slice(0, 8).map((x) => (
                <li key={x.control_id} className="flex items-center gap-2" data-testid={`mr-overdue-${x.control_id}`}>
                  <span className="font-mono text-xs text-slate-600 w-16">{x.control_id}</span>
                  <span className="text-slate-800 flex-1 truncate">{x.title}</span>
                  <span className="text-xs text-red-600">{x.target_date}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Treatments & evidence */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Risk Treatment Progress" icon={ClipboardCheck} testid="mr-treatment-progress">
          {Object.entries(t.by_status).length === 0 ? (
            <p className="text-sm text-slate-500 italic">No treatments yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {Object.entries(t.by_status).map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span className="text-slate-700">{k}</span><span className="font-mono">{v}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Evidence Status" icon={FileText} testid="mr-evidence-status"
          right={<Link to="/evidence-engine" className="text-xs text-blue-700 hover:underline">Evidence →</Link>}>
          <div className="space-y-1 text-sm">
            {Object.entries(ev.by_status).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-slate-700">{k}</span><span className="font-mono">{v}</span>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-100 text-xs text-slate-600 flex flex-wrap gap-2">
              {Object.entries(ev.by_review).map(([k, v]) => (
                <span key={k}><strong>{k}:</strong> {v}</span>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* Outstanding actions */}
      <Section title={`Outstanding Actions (${g.outstanding_actions_total})`} icon={Activity} testid="mr-outstanding-actions">
        {g.outstanding_actions.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No open gaps missing a corrective action plan.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {g.outstanding_actions.slice(0, 10).map((x) => (
              <li key={x.control_id} className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-600 w-16">{x.control_id}</span>
                <Badge className={`${PRIORITY_COLORS[x.priority] || ''} border text-[10px]`}>{x.priority}</Badge>
                <span className="text-slate-800 flex-1 truncate">{x.name}</span>
                <span className="text-xs text-slate-500">
                  {x.gap_owner ? `Owner: ${x.gap_owner}` : 'Unassigned'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Recent changes */}
      <Section title={`Important Compliance Changes (last 30 days)`} icon={Activity} testid="mr-recent-changes">
        {data.recent_changes.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No recent changes recorded.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {data.recent_changes.map((c, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-700">
                <Badge variant="outline" className="text-[10px]">{c.type}</Badge>
                <span className="font-mono">{c.control_id}</span>
                <span className="flex-1">{c.status || (c.dismissed !== undefined ? `dismissed=${c.dismissed}` : '')}</span>
                <span className="text-slate-500">{c.updated_by || ''}</span>
                <span className="text-slate-400">{(c.updated_at || '').slice(0, 16).replace('T', ' ')}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} enabled={enabled} onToggle={toggle} />
    </div>
  );
}

function SettingsDialog({ open, onOpenChange, enabled, onToggle }) {
  const [checked, setChecked] = useState(enabled);
  useEffect(() => { setChecked(enabled); }, [enabled]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mr-settings-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Management Review settings</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3 mt-3">
          <div className="pr-4">
            <div className="text-sm font-medium text-slate-900">Use New Management Review</div>
            <div className="text-xs text-slate-500 mt-1">
              Generate the Management Review live from Assessments, Gaps, Risks, Treatments & Evidence — no duplicate report tables.
            </div>
          </div>
          <Switch checked={checked} onCheckedChange={setChecked} data-testid="mr-toggle" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onToggle(checked)} data-testid="mr-settings-save-btn">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
